// ListingInfo.jsx
// Component for displaying listing title, price, category, and description - Modernized

import React from 'react';
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

// Modern SVG Icons
const TagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
    <line x1="7" y1="7" x2="7.01" y2="7"></line>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const ListingInfo = ({ listing }) => {
  // Format the created date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div style={modernStyles.container}>
      {/* Title Section */}
      <div style={modernStyles.headerSection}>
        <h1 style={modernStyles.title}>{listing.title}</h1>
        <div style={modernStyles.priceContainer}>
          <span style={modernStyles.price}>${listing.price}</span>
          {listing.originalPrice && listing.originalPrice > listing.price && (
            <span style={modernStyles.originalPrice}>${listing.originalPrice}</span>
          )}
        </div>
      </div>

      {/* Metadata Section */}
      <div style={modernStyles.metadataSection}>
        <div style={modernStyles.metadataGrid}>
          {/* Category */}
          <div style={modernStyles.metadataItem}>
            <div style={modernStyles.metadataIcon}>
              <TagIcon />
            </div>
            <div>
              <span style={modernStyles.metadataLabel}>Category</span>
              <span style={modernStyles.metadataValue}>{listing.category}</span>
            </div>
          </div>

          {/* Condition */}
          {listing.condition && (
            <div style={modernStyles.metadataItem}>
              <div style={modernStyles.metadataIcon}>
                <InfoIcon />
              </div>
              <div>
                <span style={modernStyles.metadataLabel}>Condition</span>
                <span style={modernStyles.metadataValue}>{listing.condition}</span>
              </div>
            </div>
          )}

          {/* Posted Date */}
          <div style={modernStyles.metadataItem}>
            <div style={modernStyles.metadataIcon}>
              <CalendarIcon />
            </div>
            <div>
              <span style={modernStyles.metadataLabel}>Posted</span>
              <span style={modernStyles.metadataValue}>{formatDate(listing.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      {listing.description && (
        <div style={modernStyles.descriptionSection}>
          <h3 style={modernStyles.sectionTitle}>Description</h3>
          <div style={modernStyles.description}>
            {listing.description.split('\n').map((paragraph, index) => (
              <p key={index} style={modernStyles.descriptionParagraph}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Features/Tags Section */}
      {listing.tags && listing.tags.length > 0 && (
        <div style={modernStyles.featuresSection}>
          <h3 style={modernStyles.sectionTitle}>Features</h3>
          <div style={modernStyles.tagsContainer}>
            {listing.tags.map((tag, index) => (
              <span key={index} style={modernStyles.tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Modern styles
const modernStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xxl,
  },
  
  // Header Section
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
    paddingBottom: LAYOUT.spacing.lg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  title: {
    ...TYPOGRAPHY.heading1,
    fontSize: '2rem',
    lineHeight: 1.2,
    marginBottom: 0,
    color: COLORS.text.primary,
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'baseline',
    gap: LAYOUT.spacing.md,
  },
  price: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: COLORS.primary,
    lineHeight: 1,
  },
  originalPrice: {
    fontSize: '1.25rem',
    color: COLORS.text.muted,
    textDecoration: 'line-through',
    fontWeight: '500',
  },
  
  // Metadata Section
  metadataSection: {
    paddingBottom: LAYOUT.spacing.lg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: LAYOUT.spacing.lg,
  },
  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
  },
  metadataIcon: {
    width: '40px',
    height: '40px',
    backgroundColor: COLORS.grayLight,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.primary,
  },
  metadataLabel: {
    display: 'block',
    fontSize: '0.875rem',
    color: COLORS.text.muted,
    fontWeight: '500',
    marginBottom: LAYOUT.spacing.xs,
  },
  metadataValue: {
    display: 'block',
    fontSize: '1rem',
    color: COLORS.text.primary,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  
  // Description Section
  descriptionSection: {
    paddingBottom: LAYOUT.spacing.lg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  sectionTitle: {
    ...TYPOGRAPHY.heading3,
    fontSize: '1.25rem',
    marginBottom: LAYOUT.spacing.md,
    color: COLORS.text.primary,
  },
  description: {
    lineHeight: 1.6,
  },
  descriptionParagraph: {
    ...TYPOGRAPHY.body,
    marginBottom: LAYOUT.spacing.md,
    color: COLORS.text.secondary,
  },
  
  // Features Section
  featuresSection: {
    paddingBottom: LAYOUT.spacing.lg,
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: LAYOUT.spacing.sm,
  },
  tag: {
    backgroundColor: COLORS.accent,
    color: COLORS.text.onYellow,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.md}`,
    borderRadius: LAYOUT.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
};

export default ListingInfo;